// Archives (v1.39, app.md §9). A read-only lookup of terminal invoices — the two
// Point Click Care completions and the three archived states. No work actions:
// each card carries only Open, and the detail modal is view-only. Newest-first.
//
// Two things set it apart from the queue screens:
//  - It never loads the whole set (terminal rows grow without bound). It pages
//    one screenful at a time, with a Load more button.
//  - The shared header filter is applied server-side, so filtering and paging
//    compose — the queue screens filter their loaded page in memory, which would
//    be wrong across pages here.
//
// PORT NOTE: the filter used to be compiled into a query string here and the page
// cursor was the store's own paging token. Both are the API's business now: named
// filter parameters go in, an opaque cursor comes back, and the client never looks
// inside it.
//
// Row visibility is the API's, not app logic: a company-scoped viewer sees only
// their company's completed invoices; internal roles see all. The PDF pane shows
// only where the profile allows document access (canPreviewPdf), which since
// 2026-08-10 includes a company approver at any of the three tiers (owner ruling
// R2; docs/security.md §5 owns the policy and the exposure it accepts).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Body1, Button, Caption1, makeStyles, tokens } from '@fluentui/react-components'
import { ArrowDownRegular } from '@fluentui/react-icons'
import { ScreenTitle } from '../components/ScreenTitle'
import type { Invoice } from '../lib/types'
import { canPreviewPdf, type Session } from '../lib/identity'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { loadArchivePage } from '../lib/data'
import { errorMessage } from '../api/errors'
import { isFilterActive, type InvoiceFilter } from '../lib/filters'
import { TERMINAL_STATUSES } from '../lib/metrics'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

// One screenful per fetch — enough to fill the viewport, small enough that an
// accidental Archives selection never pulls thousands of rows.
const PAGE_SIZE = 50

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
  more: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '20px 0 4px' },
  count: { color: tokens.colorNeutralForeground3 },
})

interface Props {
  session: Session
  /** Bumped by the top-bar Refresh — reloads page 1. */
  refreshNonce?: number
  /** Page-wide filter (header Filter button), applied server-side here. */
  filter: InvoiceFilter
}

export function ArchivesScreen({ session, refreshNonce, filter }: Props) {
  const s = useStyles()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)

  const showPdf = useMemo(() => canPreviewPdf(session), [session])
  // Stable key for the filter, so page 1 reloads when the filter changes without
  // re-running on every render from a new object identity.
  const filterKey = JSON.stringify(filter)

  // Bumped on every page-1 load. A loadMore that resolves after the filter
  // changed is discarded, so a stale page can't append old-filter rows or
  // overwrite the new query's cursor.
  const queryVersion = useRef(0)

  // Page 1: on mount, whenever the filter changes, and on the top-bar Refresh.
  const loadFirst = useCallback(async () => {
    const version = ++queryVersion.current
    setLoading(true)
    setError(null)
    try {
      const page = await loadArchivePage(TERMINAL_STATUSES, filter, undefined, PAGE_SIZE)
      if (queryVersion.current !== version) return // superseded by a newer load
      setInvoices(page.rows)
      setCursor(page.cursor)
    } catch (e) {
      if (queryVersion.current === version) setError(errorMessage(e))
    } finally {
      if (queryVersion.current === version) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on filterKey (stable serialization), not the filter object identity
  }, [filterKey])

  useEffect(() => {
    loadFirst()
  }, [loadFirst, refreshNonce])

  async function loadMore() {
    if (!cursor) return
    const version = queryVersion.current
    setLoadingMore(true)
    setError(null)
    try {
      const page = await loadArchivePage(TERMINAL_STATUSES, filter, cursor, PAGE_SIZE)
      if (queryVersion.current !== version) return // filter changed mid-flight — discard
      setInvoices((prev) => [...prev, ...page.rows])
      setCursor(page.cursor)
    } catch (e) {
      if (queryVersion.current === version) setError(errorMessage(e))
    } finally {
      if (queryVersion.current === version) setLoadingMore(false)
    }
  }

  const filtered = isFilterActive(filter)

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Archives</ScreenTitle>
        <Body1 className={s.sub}>Look up invoices that have finished processing — exported to Point Click Care, or archived.</Body1>
      </div>

      <ErrorBanner message={error} />

      {loading ? (
        <Loading label="Loading archived invoices…" />
      ) : invoices.length === 0 ? (
        <EmptyQueue text={filtered ? 'No invoices match the current filters.' : 'Nothing in the archive yet.'} />
      ) : (
        <>
          <InvoiceQueue invoices={invoices} columns="amounts" onOpen={setOpenInvoice} />
          <div className={s.more}>
            <Caption1 className={s.count}>
              Showing {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
              {cursor ? ' · more available' : ''}
            </Caption1>
            {cursor && (
              <Button icon={<ArrowDownRegular />} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            )}
          </div>
        </>
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={showPdf}
        user={session.user}
        onClose={() => setOpenInvoice(null)}
      />
    </div>
  )
}
